import { useContext, useEffect, useState } from "react";

import { Chip, TextField, Paper, Autocomplete } from "@mui/material";
import {
  createTagRequest,
  deleteTag as deleteTagRequest,
  patchTagRequest,
  Tag,
} from "@rubi/api-generated";
import { toast } from "@rubi/ui";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";

import { SingleAlertContext } from "@/components/alerts/context";

import styles from "./ManageTags.module.css";

const predefinedTags = [
  { title: "Suspect 1", value: "Suspect 1" },
  { title: "Suspect 2", value: "Suspect 2" },
  { title: "Suspect 3", value: "Suspect 3" },
  { title: "Suspect 4", value: "Suspect 4" },
  { title: "Suspect 5", value: "Suspect 5" },
  { title: "Vehicle 1", value: "Vehicle 1" },
  { title: "Vehicle 2", value: "Vehicle 2" },
  { title: "Vehicle 3", value: "Vehicle 3" },
];

export const ManageTags = ({ bulletinID }: { bulletinID: number }) => {
  const { t } = useTranslation("bulletin/create/upload");
  const [tagInput, setTagInput] = useState<string | null>(null);
  const [descInput, setDescInput] = useState<{ [id: string]: string }>({});
  const [searchParams] = useSearchParams();

  const { alertProperties, setAlertProperty } = useContext(SingleAlertContext);
  const { tagDict } = alertProperties;
  const updateTagDictInContext = (tagDict: any) => {
    setAlertProperty("tagDict", tagDict);
  };

  useEffect(() => {
    // If tags exist, prepare description object
    if (Object.keys(tagDict).length > 0) {
      const descriptionInput = {};
      for (const tagId of Object.keys(tagDict)) {
        // if descriptionFr exists, use it, otherwise use description
        descriptionInput[tagId] =
          tagDict[tagId].description_fr || tagDict[tagId].description;
      }
      setDescInput(descriptionInput);
    }
  }, []);

  // Process tags from URL parameters
  useEffect(() => {
    const tags = searchParams.get("tags");
    if (tags && bulletinID) {
      try {
        const parsedTags = JSON.parse(decodeURIComponent(tags));

        // Process each tag
        Object.values(parsedTags).forEach(async (tag: any) => {
          // Check if tag already exists
          const tagExists = Object.values(tagDict).some(
            (existingTag: any) => existingTag.title === tag.title,
          );

          if (!tagExists) {
            try {
              // Create the tag using existing addTag function logic
              const createRes = await createTagRequest({
                path: { bulletin_id: bulletinID },
                body: { title: tag.title },
              });
              const newTag: Tag = createRes.data!;

              // Update tagDict
              const updatedTagDict = { ...tagDict, [newTag.id]: newTag };
              updateTagDictInContext(updatedTagDict);

              // If tag has description, update it
              if (tag.description) {
                await patchTagRequest({
                  path: { bulletin_id: bulletinID, tag_id: newTag.id },
                  body: {
                    title: newTag.title,
                    description: tag.description,
                    attachments: newTag.attachments,
                  },
                });

                // Update local description input
                setDescInput((prev) => ({
                  ...prev,
                  [newTag.id]: tag.description,
                }));
              } else {
                setDescInput((prev) => ({ ...prev, [newTag.id]: "" }));
              }
            } catch (error) {
              console.error("Failed to create tag:", tag.title, error);
            }
          }
        });

        // Clear URL parameters after processing
        const newUrl = window.location.pathname;
        window.history.replaceState({}, "", newUrl);
      } catch (e) {
        console.error("Failed to parse tags from URL:", e);
      }
    }
  }, [bulletinID, searchParams]);

  const addTag = async (tagTitle: string) => {
    for (const tagId of Object.keys(tagDict)) {
      if (tagDict[tagId].title === tagTitle) {
        toast.warning(t("toast.warning.tagAlreadyExists"));
        return;
      }
    }

    createTagRequest({
      path: { bulletin_id: bulletinID },
      body: { title: tagTitle },
    }).then((res) => {
      const newTag: Tag = res.data!;
      updateTagDictInContext({ ...tagDict, [newTag.id]: newTag });
      setDescInput((prev) => ({ ...prev, [newTag.id]: "" }));
    });
    setTagInput(null);
  };

  const updateDescription = async (tagID: string) => {
    const tag = tagDict[tagID] as Tag;
    patchTagRequest({
      path: { bulletin_id: bulletinID, tag_id: tagID },
      body: {
        title: tag.title,
        description: descInput[tagID],
        attachments: tag.attachments,
      },
    }).then((res) => {
      updateTagDictInContext({ ...tagDict, [tagID]: res.data! });
    });
  };

  const deleteTag = async (tagID: string) => {
    deleteTagRequest({ path: { bulletin_id: bulletinID, tag_id: tagID } }).then(
      () => {
        const updatedTagDict = { ...tagDict };
        delete updatedTagDict[tagID];
        updateTagDictInContext(updatedTagDict);

        const updatedDescInput = { ...descInput };
        delete updatedDescInput[tagID];
        setDescInput(updatedDescInput);
      },
    );
  };

  return (
    <Paper elevation={4} style={{ padding: "24px 20px" }}>
      <div className={styles.tagsContainer}>
        {Object.keys(tagDict).map((tagID) => (
          <div key={tagID} className={styles.oneTagContainer}>
            <Chip
              label={tagDict[tagID].title}
              onDelete={() => deleteTag(tagID)}
              style={{ maxWidth: "160px" }}
              title={tagDict[tagID].title}
            />
            <TextField
              label={t("tagDescription")}
              value={descInput[tagID] || ""}
              onChange={(e) =>
                setDescInput((prev) => ({ ...prev, [tagID]: e.target.value }))
              }
              fullWidth
              multiline
              margin="none"
              variant="outlined"
              minRows={3}
              className="mt-3"
              onBlur={() => updateDescription(tagID)}
            />
          </div>
        ))}
      </div>

      <Autocomplete
        value={tagInput}
        freeSolo
        autoComplete
        clearOnBlur
        onChange={(_event, newValue) => {
          if (typeof newValue === "string" && newValue.trim() !== "") {
            addTag(newValue); // Handle free-solo input
          } else if (
            newValue &&
            typeof newValue === "object" &&
            "value" in newValue
          ) {
            addTag(newValue.value); // Handle predefined tag selection
          }
          setTagInput(null);
        }}
        filterOptions={(options, { inputValue }) => {
          const filtered = options.filter((option) =>
            option.title.toLowerCase().includes(inputValue.toLowerCase()),
          );

          // Add "Add 'xxx'" option if input doesn't match any predefined tag
          if (
            inputValue.trim() !== "" &&
            !options.some((opt) => opt.value === inputValue)
          ) {
            filtered.push({ title: `Add '${inputValue}'`, value: inputValue });
          }

          return filtered;
        }}
        options={predefinedTags.filter(
          (tag) =>
            !Object.values(tagDict).find(
              (existingTag) => (existingTag as Tag).title === tag.title,
            ),
        )}
        getOptionLabel={(option) =>
          typeof option === "string" ? option : option.title
        }
        isOptionEqualToValue={(option, value) =>
          typeof option === "string"
            ? option === value
            : option.value === value?.value
        }
        className={styles.addTagContainer}
        handleHomeEndKeys
        renderInput={(params) => (
          <TextField
            {...params}
            label={t("addTag")}
            variant="outlined"
            placeholder={t("addTagPlaceholder")}
          />
        )}
      />
    </Paper>
  );
};
